import {
  ChevronLeft,
  ChevronRight,
  Expand,
  FileSpreadsheet,
  MoreVertical,
  Plus,
  Scan,
  Search,
  Trash,
  X,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Info,
  Archive,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import {
  deleteEquipment,
  getAllEquipment,
  getEquipmentModels,
  getEquipmentOwner,
  getEquipmentTypes,
  insertNewEquipment,
} from "../services/equipmentsAPI";
import { transformSelectFormat } from "../utils/transformFormatSelect";
import NewStatusModal from "../components/common/NewStatusModal";

const TABLE_HEADERS = [
  "Equipment",
  "Equipment Type",
  "Equipment Model",
  "Owner",
  "Contrator / SCGL",
  "Decommission",
];
const RECORD_COUNT_OPTIONS = [
  { value: "10", label: "10" },
  { value: "15", label: "15" },
  { value: "20", label: "20" },
  { value: "25", label: "25" },
  { value: "30", label: "30" },
  { value: "50", label: "50" },
];

const DEFAULT_CONTRACTOR_SCGL = [
  { value: "Contrator", label: "Contrator" },
  { value: "SCGL", label: "SCGL" },
];

const MONTH_INDEX_BY_SHORT_NAME = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

const DEFAULT_FILTERS = {
  equipmentType: "",
  equipmentModel: "",
  equipmentOwner: "",
  equipment: "",
};

const DEFAULT_NEW_EQUIPMENT_FORM = {
  equipmentId: "",
  equipmentName: "",
  equipmentTypeId: "",
  equipmentModelId: "",
  contractorScgl: "",
  equipmentOwner: "",
};

const DEFAULT_INFORMATION_MODAL = {
  isOpen: false,
  informationMessage: "",
  title: "",
  type: "",
};

function normalizeText(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function parseDateRangeValue(value) {
  const normalized = String(value ?? "").trim();
  if (!normalized) {
    return null;
  }

  const parts = normalized.split(" - ");
  if (parts.length !== 2) {
    return null;
  }

  const startDate = parseDatePart(parts[0], false);
  const endDate = parseDatePart(parts[1], true);

  if (!startDate || !endDate) {
    return null;
  }

  return {
    startDate,
    endDate,
  };
}

function parseDatePart(label, endOfDay) {
  const matcher = /^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{2,4})$/;
  const match = label.match(matcher);
  if (!match) {
    return null;
  }

  const day = Number(match[1]);
  const monthIndex = MONTH_INDEX_BY_SHORT_NAME[String(match[2]).toLowerCase()];
  const yearCandidate = Number(match[3]);

  if (
    !Number.isFinite(day) ||
    !Number.isFinite(monthIndex) ||
    !Number.isFinite(yearCandidate)
  ) {
    return null;
  }

  const year = yearCandidate < 100 ? 2000 + yearCandidate : yearCandidate;
  return new Date(
    year,
    monthIndex,
    day,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0,
  );
}

function parseRowDateValue(value) {
  const normalized = String(value ?? "").trim();
  if (!normalized || normalized === "-") {
    return null;
  }

  const slashMatcher =
    /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:\s+(\d{1,2})(?::(\d{2}))?)?$/;
  const slashMatch = normalized.match(slashMatcher);
  if (slashMatch) {
    const day = Number(slashMatch[1]);
    const monthIndex = Number(slashMatch[2]) - 1;
    const yearCandidate = Number(slashMatch[3]);
    const hours = Number(slashMatch[4] ?? "0");
    const minutes = Number(slashMatch[5] ?? "0");
    const year = yearCandidate < 100 ? 2000 + yearCandidate : yearCandidate;

    if (
      Number.isFinite(day) &&
      Number.isFinite(monthIndex) &&
      monthIndex >= 0 &&
      monthIndex <= 11 &&
      Number.isFinite(year) &&
      Number.isFinite(hours) &&
      Number.isFinite(minutes)
    ) {
      const parsedDate = new Date(year, monthIndex, day, hours, minutes, 0, 0);
      if (!Number.isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }
  }

  const directDate = new Date(normalized);
  if (!Number.isNaN(directDate.getTime())) {
    return directDate;
  }

  const matcher =
    /^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{2,4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i;
  const match = normalized.match(matcher);
  if (!match) {
    return null;
  }

  const day = Number(match[1]);
  const monthIndex = MONTH_INDEX_BY_SHORT_NAME[String(match[2]).toLowerCase()];
  const yearCandidate = Number(match[3]);
  const rawHours = Number(match[4]);
  const minutes = Number(match[5]);
  const meridian = String(match[6]).toUpperCase();

  if (
    !Number.isFinite(day) ||
    !Number.isFinite(monthIndex) ||
    !Number.isFinite(yearCandidate) ||
    !Number.isFinite(rawHours) ||
    !Number.isFinite(minutes)
  ) {
    return null;
  }

  let hours = rawHours;
  if (meridian === "AM" && hours === 12) {
    hours = 0;
  }
  if (meridian === "PM" && hours < 12) {
    hours += 12;
  }

  const year = yearCandidate < 100 ? 2000 + yearCandidate : yearCandidate;
  return new Date(year, monthIndex, day, hours, minutes, 0, 0);
}

function getStateBadgeClass(state) {
  if (state === "Operational") {
    return "bg-[#DDFCE8] text-[#149B57]";
  }

  if (state === "Stopped") {
    return "bg-[#FEE2E2] text-[#B42318]";
  }

  return "bg-[#FEF7E8] text-[#B54708]";
}

function ModalEquipmentTypeField({ label, value, options, onChange, isTop }) {
  return (
    <div>
      <p className="mb-2 text-[14px] font-medium text-[#6A7282]">{label}</p>
      <Select
        defaultValue={value}
        onChange={onChange}
        options={options}
        menuPlacement={isTop ? "top" : "auto"}
      />
    </div>
  );
}

function ModalInformative({
  title,
  onClose,
  isOpen,
  onSave,
  onChange,
  cancelLabel,
  saveLabel,
  children,
}) {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-55 flex justify-center bg-[rgba(17,24,39,0.35)] p-4">
      <div className="w-full max-w-[800px] max-h-[200px] overflow-visible rounded-[18px] border border-[#D7DEE8] bg-white shadow-[0_30px_60px_rgba(16,24,40,0.28)]">
        <header className="flex items-center justify-between rounded-t-[18px] bg-[#E8EFFA] px-6 py-2">
          <h3 className="text-[15px] font-semibold text-brand-500">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#6A7282] hover:bg-[#DCE6F6]"
            aria-label="Close modal"
          >
            <X size={22} />
          </button>
        </header>

        <div className="space-y-5 px-6 py-2">{children}</div>

        <footer className="flex items-center justify-end gap-8 border-t border-[#E5E7EB] px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center gap-2 h-12 min-w-[160px] rounded-xl bg-[#D81D1D] px-5 text-[16px] font-semibold text-white shadow-sm hover:bg-[#C21313]"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onSave}
            className="flex items-center justify-center gap-2 h-12 min-w-[160px] rounded-xl bg-[#2563EB] px-5 text-[16px] font-semibold text-white shadow-sm hover:bg-[#1D4ED8]"
          >
            {saveLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}

function GenericModal({
  isOpen,
  title,
  onClose,
  children,
  footer,
  type,
  tooltipType,
  needIcon,
}) {
  if (!isOpen) return null;

  const modalConfig = {
    success: {
      header: "bg-green-100",
      text: "text-green-700",
      icon: CheckCircle,
    },
    warning: {
      header: "bg-red-100",
      text: "text-red-700",
      icon: AlertTriangle,
    },
    info: {
      header: "bg-blue-100",
      text: "text-blue-700",
      icon: Info,
    },
  };

  const config = modalConfig[type];
  const Icon = config.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(17,24,39,0.35)] p-4">
      <div className="w-full max-w-[700px] max-h-[400px] rounded-[18px] border border-[#D7DEE8] bg-white shadow-[0_30px_60px_rgba(16,24,40,0.28)]">
        <header
          className={`flex items-center justify-between rounded-t-[18px] px-6 py-3 ${config.header}`}
        >
          {needIcon && (
            <div className="relative group">
              <Icon size={22} className={config.text} />

              <div
                className="
      absolute left-8 top-1/2 -translate-y-1/2
      whitespace-nowrap
      rounded-md bg-gray-900 px-2 py-1
      text-xs text-white
      opacity-0 shadow-lg
      transition-opacity
      group-hover:opacity-100
      z-50
    "
              >
                {tooltipType}
              </div>
            </div>
          )}

          <h3 className="text-[15px] font-semibold text-brand-500">{title}</h3>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#6A7282]"
          >
            <X size={22} />
          </button>
        </header>

        <div className="space-y-5 px-6 py-4">{children}</div>

        {footer && (
          <footer className="flex items-center justify-end gap-4 border-t border-[#E5E7EB] px-6 py-4">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

export const EquipmentManagment = () => {
  const [isLoadingRows, setIsLoadingRows] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowsError, setRowsError] = useState(null);
  const [rowsInfo, setRowsInfo] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState({
    value: "25",
    label: "25",
  });
  const [searchValue, setSearchValue] = useState("");

  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  const [equipmentList, setEquipmentList] = useState([]);

  const [selectedEquipmentTypeFilter, setSelectedEquipmentTypeFilter] =
    useState();
  const [equipmentTypeList, setEquipmentTypeList] = useState([]);

  const [selectedEquipmentModelFilter, setSelectedEquipmentModelFilter] =
    useState();
  const [equipmentModelListMain, setEquipmentModelListMain] = useState([]);
  const [equipmentModelList, setEquipmentModelList] = useState([]);
  const [equipmentTypeModelModal, setEquipmentModelModal] = useState([]);

  const [selectedEquipmentOwnerFilter, setSelectedEquipmentOwnerFilter] =
    useState();
  const [equipmentOwnerList, setEquipmentOwnerList] = useState([]);

  const [newStatusForm, setNewStatusForm] = useState(
    DEFAULT_NEW_EQUIPMENT_FORM,
  );

  const [isAddStatusOpen, setIsAddStatusOpen] = useState(false);

  const [informativeModalIsOpen, setInformativeModalIsOpen] = useState(false);
  const [saveModalIsOpen, setSaveModalIsOpen] = useState(false);

  const [errorModalIsOpen, setErrorModalIsOpen] = useState(
    DEFAULT_INFORMATION_MODAL,
  );

  const [modalIsLoading, setModalIsLoading] = useState(false);

  const [isSavingStatus, setIsSavingStatus] = useState(false);

  const [reloadPage, setReloadPage] = useState(false);

  const [selectedEquipmentInfo, setSelectedEquipmentInfo] = useState(null);

  const filteredRows = useMemo(() => {
    const normalizedEquipmentType = normalizeText(appliedFilters.equipmentType);
    const normalizedEquipment = normalizeText(appliedFilters.equipmentModel);
    const normalizedReason = normalizeText(appliedFilters.getEquipmentOwner);
    const normalizedSearch = normalizeText(searchValue);

    return equipmentList.filter((row) => {
      if (
        normalizedEquipmentType &&
        normalizeText(row.equipmentType) !== normalizedEquipmentType
      ) {
        return false;
      }

      if (
        normalizedEquipment &&
        normalizeText(row.equipment) !== normalizedEquipment
      ) {
        return false;
      }
      if (normalizedSearch) {
        const rowValues = [
          row.equipment,
          row.equipmentType,
          row.state,
          row.category,
          row.subcategory,
          row.reason,
          row.shift,
          row.comment,
          row.startDate,
          row.endDate,
          row.duration,
        ];

        const matchesSearch = rowValues.some((value) =>
          normalizeText(value).includes(normalizedSearch),
        );

        if (!matchesSearch) {
          return false;
        }
      }

      return true;
    });
  }, [appliedFilters, rows, searchValue, equipmentList]);

  const recordsPerPageValue = useMemo(() => {
    const parsedValue = Number(recordsPerPage.value);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 10;
  }, [recordsPerPage, equipmentList]);

  const paginatedRows = useMemo(() => {
    const startIndex = (activePage - 1) * recordsPerPageValue;
    return filteredRows.slice(startIndex, startIndex + recordsPerPageValue);
  }, [activePage, filteredRows, recordsPerPageValue]);

  const totalRecords = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPageValue));

  const visiblePageNumbers = useMemo(() => {
    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    let startPage = Math.max(1, activePage - 1);
    let endPage = Math.min(totalPages, startPage + 2);
    startPage = Math.max(1, endPage - 2);

    return Array.from(
      { length: endPage - startPage + 1 },
      (_, index) => startPage + index,
    );
  }, [activePage, totalPages, equipmentList]);

  function handleApplyFilters() {
    //setAppliedFilters(filters);
    //setActivePage(1);
    console.log("[StatusEditorPage] Applied filters:");
  }

  function updateNewStatusField(field, value) {
    setNewStatusForm((previousForm) => ({ ...previousForm, [field]: value }));
    console.log("Respuesta ins: ", newStatusForm);
  }

  function handleCloseModal() {
    setIsAddStatusOpen(false);
    setNewStatusForm(DEFAULT_NEW_EQUIPMENT_FORM);
    setEquipmentModelModal(equipmentModelListMain);
  }

  function handleCloseModalInformative() {
    setInformativeModalIsOpen(false);
  }

  async function handleSaveEquipment() {
    if (isSavingStatus) {
      return;
    }
    try {
      setIsSavingStatus(true);
      setRowsError(null);

      console.log("Respuesta insert: ", newStatusForm);

      if (
        !newStatusForm.equipmentName ||
        !newStatusForm.equipmentTypeId ||
        !newStatusForm.equipmentModelId ||
        !newStatusForm.equipmentOwner ||
        !newStatusForm.contractorScgl
      ) {
        setErrorModalIsOpen({
          isOpen: true,
          informationMessage: "All fields are required.",
          title: "Warning",
          type: "warning",
        });
        return;
      }

      const regex = /^[A-Za-z]{1,3}\d+$/;
      if (!regex.test(newStatusForm.equipmentName)) {
        setErrorModalIsOpen({
          isOpen: true,
          informationMessage: "The equipment name is invalid.",
          title: "Warning",
          type: "warning",
        });
        return;
      }

      const equipmentNameUpperCase = newStatusForm.equipmentName.toUpperCase();

      const newEquipmentId = newStatusForm.equipmentName.replace(/\D/g, "");
      console.log("Insert ", newEquipmentId);
      updateNewStatusField("equipmentName", equipmentNameUpperCase);
      updateNewStatusField("equipmentId", newEquipmentId);

      setSaveModalIsOpen(true);

      //const resultInsert = await insertNewEquipment(newStatusForm);
      //console.log("Respuesta ins api: ", resultInsert);

      //setIsAddStatusOpen(false)
      //setNewStatusForm(DEFAULT_NEW_EQUIPMENT_FORM)
    } catch (error) {
      setRowsError(
        error instanceof Error ? error.message : "Failed to create status.",
      );
    } finally {
      setIsSavingStatus(false);
    }
  }

  async function saveNewEquipment() {
    setErrorModalIsOpen(DEFAULT_INFORMATION_MODAL);
    setModalIsLoading(true);
    setSaveModalIsOpen(false);
    const resultInsert = await insertNewEquipment(newStatusForm);
    console.log("Respuesta ins api: ", resultInsert);

    setModalIsLoading(false);
    if (resultInsert.status == "success") {
      setIsAddStatusOpen(false);
      setNewStatusForm(DEFAULT_NEW_EQUIPMENT_FORM);
      setErrorModalIsOpen({
        isOpen: true,
        informationMessage: "The equipment was saved",
        title: "Success",
        type: "success",
      });
      setReloadPage(!reloadPage);
    } else {
      setErrorModalIsOpen({
        isOpen: true,
        informationMessage: "The equipment was not saved",
        title: "Warning",
        type: "warning",
      });
    }
  }

  async function desactivateEquipment(isLastDay) {
    setErrorModalIsOpen(DEFAULT_INFORMATION_MODAL);
    setModalIsLoading(true);
    console.log("Respuesta ins api: ", selectedEquipmentInfo);
    if (selectedEquipmentInfo.id) {
      const resultInsert = await deleteEquipment({
        equipmentId: selectedEquipmentInfo.id,
        isLastDay: isLastDay,
      });
      const resultDesactivate = await deleteEquipment(newStatusForm);
      console.log("Respuesta ins api: ", resultDesactivate);
      setModalIsLoading(false);
      if (resultInsert.status == "success") {
        setInformativeModalIsOpen(false);
        setErrorModalIsOpen({
          isOpen: true,
          informationMessage:
            "The " + selectedEquipmentInfo.equipment + " was decommissioned",
          title: "Success",
          type: "success",
        });
        setReloadPage(!reloadPage);
      } else {
        setErrorModalIsOpen({
          isOpen: true,
          informationMessage:
            "The " + selectedEquipmentInfo.equipmen + " was not decommissioned",
          title: "Warning",
          type: "warning",
        });
      }
    }
  }

  async function handleChangeEquipmentTypeModal(value) {
    updateNewStatusField("equipmentTypeId", value);
    console.log("Cambio el equipmentTypeId", value);
    const equipmentModel = await getEquipmentModels([value.value]);
    console.log("Cambio el equipmentTypeId", equipmentModel);

    var formatEquipmentModel = transformSelectFormat(
      equipmentModel?.data,
      "equipment_model_id",
      "name",
    );
    console.log("Respuesta 2: ", formatEquipmentModel);
    if (formatEquipmentModel.status == 200) {
      setEquipmentModelModal(formatEquipmentModel.data);
    }
  }

  function handleDesactivateEquipment(equipmentInfo) {
    console.log("El equipo se va a desactivar: ", equipmentInfo);
    setInformativeModalIsOpen(true);
    setSelectedEquipmentInfo(equipmentInfo);
  }

  function handleClearFilters() {
    setSelectedEquipmentTypeFilter(null);
    setSelectedEquipmentModelFilter(null);
    setSelectedEquipmentOwnerFilter(null);
    setEquipmentModelList(equipmentModelListMain);
  }

  useEffect(() => {
    (async () => {
      const equipmentTypes = await getEquipmentTypes();
      const equipmentModel = await getEquipmentModels([]);
      const equipmentOwner = await getEquipmentOwner();

      const allEquipment = await getAllEquipment(null, null, null);

      setEquipmentList(allEquipment.data);

      const formattedRows = allEquipment.data.map((equipment) => ({
        id: equipment.equipment_id,
        equipment: equipment.equipment_name,
        equipmentType: equipment.equipment_type_name,
        equipmentModel: equipment.equipment_model,
        owner: equipment.equipment_owner,
        contractorOrScgl: equipment.contractor_or_scgl,
        state: equipment.status,
        reason: equipment.reason,
        startDate: equipment.start_date,
      }));

      setEquipmentList(formattedRows);

      console.log("Respuesta equipment filter: ", formattedRows);
      var formatEquipmentTypes = transformSelectFormat(
        equipmentTypes?.data,
        "equipment_type_id",
        "equipment_type_name",
      );
      console.log("Respuesta 2: ", formatEquipmentTypes);
      if (formatEquipmentTypes.status == 200) {
        setEquipmentTypeList(formatEquipmentTypes.data);
      }

      var formatEquipmentModel = transformSelectFormat(
        equipmentModel?.data,
        "equipment_model_id",
        "name",
      );
      console.log("Respuesta 2: ", formatEquipmentModel);
      if (formatEquipmentModel.status == 200) {
        setEquipmentModelListMain(formatEquipmentModel.data);
        setEquipmentModelList(formatEquipmentModel.data);
        setEquipmentModelModal(formatEquipmentModel.data);
      }

      var formatEquipmentOwner = transformSelectFormat(
        equipmentOwner?.data,
        "equipment_owner",
        "equipment_owner",
      );
      console.log("Respuesta 2: ", formatEquipmentOwner);
      if (formatEquipmentOwner.status == 200) {
        setEquipmentOwnerList(formatEquipmentOwner.data);
      }
    })();
  }, []);

  useEffect(() => {
    let filterEquipmentType = selectedEquipmentTypeFilter
      ? [selectedEquipmentTypeFilter.value]
      : null;
    let filterEquipmentModel = selectedEquipmentModelFilter
      ? [selectedEquipmentModelFilter.value]
      : null;
    let filterEquipmentOwner = selectedEquipmentOwnerFilter
      ? [selectedEquipmentOwnerFilter.value]
      : null;

    (async () => {
      const allEquipment = await getAllEquipment(
        filterEquipmentType,
        filterEquipmentModel,
        filterEquipmentOwner,
      );

      const formattedRows = allEquipment.data.map((equipment) => ({
        id: equipment.equipment_id,
        equipment: equipment.equipment_name,
        equipmentType: equipment.equipment_type_name,
        equipmentModel: equipment.equipment_model,
        owner: equipment.equipment_owner,
        contractorOrScgl: equipment.contractor_or_scgl,
        state: equipment.status,
        reason: equipment.reason,
        startDate: equipment.start_date,
      }));

      setEquipmentList(formattedRows);
      setActivePage(1);
    })();
  }, [
    selectedEquipmentTypeFilter,
    selectedEquipmentModelFilter,
    selectedEquipmentOwnerFilter,
    reloadPage,
  ]);

  useEffect(() => {
    let filterEquipmentType = selectedEquipmentTypeFilter
      ? [selectedEquipmentTypeFilter.value]
      : null;
    (async () => {
      const equipmentModel = await getEquipmentModels(filterEquipmentType);
      console.log("Cambio el equipmentTypeId", equipmentModel);

      var formatEquipmentModel = transformSelectFormat(
        equipmentModel?.data,
        "equipment_model_id",
        "name",
      );
      console.log("Respuesta 2: ", formatEquipmentModel);
      if (formatEquipmentModel.status == 200) {
        setEquipmentModelList(formatEquipmentModel.data);
      }
    })();
  }, [selectedEquipmentTypeFilter]);

  //var data_login = await authLogin(usernameLogin, passwordLogin);
  return (
    <>
      <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-300 bg-white">
        <header className="flex items-center justify-between border-b border-gray-300 px-4 py-3">
          <h1 className="fms-page-title"> </h1>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <section className="border-b border-[#E5E7EB] px-4 py-4">
            <h2 className="mb-4 text-[18px] font-semibold text-[#394150]">
              Filters
            </h2>
            <div className="grid gap-5 md:grid-cols-5">
              <div>
                <p className="mb-2 text-[14px] font-medium text-[#6A7282]">
                  Equipment Type
                </p>
                <Select
                  value={selectedEquipmentTypeFilter}
                  onChange={setSelectedEquipmentTypeFilter}
                  options={equipmentTypeList}
                />
              </div>
              <div>
                <p className="mb-2 text-[14px] font-medium text-[#6A7282]">
                  Equipment Model
                </p>
                <Select
                  value={selectedEquipmentModelFilter}
                  onChange={setSelectedEquipmentModelFilter}
                  options={equipmentModelList}
                />
              </div>
              <div>
                <p className="mb-2 text-[14px] font-medium text-[#6A7282]">
                  Owner
                </p>
                <Select
                  value={selectedEquipmentOwnerFilter}
                  onChange={setSelectedEquipmentOwnerFilter}
                  options={equipmentOwnerList}
                />
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="h-10 min-w-[160px] rounded-xl border border-[#D7DEE8] bg-white px-5 text-[14px] font-semibold text-[#4A5565] hover:bg-[#F8FAFC]"
                >
                  Clear Filters
                </button>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => setIsAddStatusOpen(true)}
                  className="flex items-center justify-center gap-2 h-10 min-w-[160px] rounded-xl bg-[#2563EB] px-5 text-[16px] font-semibold text-white shadow-sm hover:bg-[#1D4ED8]"
                >
                  <Plus size={20} />
                  <span>Add Equipment</span>
                </button>
              </div>
            </div>
          </section>

          <section>
            <div className="mb-4 flex flex-wrap items-center gap-3 px-4 py-2"></div>

            {rowsError ? (
              <p className="mb-3   rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-[13px] font-medium text-[#B42318]">
                {rowsError}
              </p>
            ) : null}
            {!rowsError && rowsInfo ? (
              <p className="mb-3 rounded-xl border border-[#D7DEE8] bg-[#F8FAFC] px-3 py-2 text-[13px] font-medium text-[#4A5565] ">
                {rowsInfo}
              </p>
            ) : null}
            <div className="px-2">
              <div className="min-h-[320px] overflow-x-auto rounded-2xl border border-[#D7DEE8] ">
                <table className="w-full  min-w-[1120px] table-fixed border-collapse">
                  <colgroup>
                    <col className="w-[14%]" />
                    <col className="w-[18%]" />
                    <col className="w-[22%]" />
                    <col className="w-[14%]" />
                    <col className="w-[18%]" />
                    <col className="w-[14%]" />
                  </colgroup>
                  <thead className="bg-[#F1F5F9]">
                    <tr className="border-b border-[#D7DEE8]">
                      {TABLE_HEADERS.map((header, index) => (
                        <th
                          key={`${header}-${index}`}
                          className="px-3 py-3 text-center text-[13px] font-semibold text-[#4A5565]"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {isLoadingRows ? (
                      <tr>
                        <td
                          colSpan={TABLE_HEADERS.length}
                          className="px-3 py-6 text-center text-[14px] font-medium text-[#6A7282]"
                        >
                          Cargando registros...
                        </td>
                      </tr>
                    ) : null}
                    {!isLoadingRows &&
                    !rowsError &&
                    paginatedRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={TABLE_HEADERS.length}
                          className=" px-3 py-6 text-center text-[14px] font-medium text-[#6A7282]"
                        >
                          No records found.
                        </td>
                      </tr>
                    ) : null}
                    {!isLoadingRows && !rowsError
                      ? paginatedRows.map((row) => (
                          <tr
                            key={row.id}
                            className="border-b border-[#E5E7EB] last:border-b-0 hover:bg-[#F8FAFC] text-center"
                          >
                            <td className="whitespace-nowrap px-3 py-3 text-[14px] text-[#1D293D] text-center">
                              {row.equipment}
                            </td>
                            <td className="px-3 py-3 text-[14px] text-[#1D293D] text-center">
                              <span className="inline-flex items-center gap-2">
                                {/*
                              <img
                                src={getEquipmentTypeIcon(row.equipmentType)}
                                alt=""
                                className="h-5 w-5"
                              />
                              */}
                                <span>{row.equipmentType}</span>
                              </span>
                            </td>
                            <td className=" px-3 py-3 text-[14px] text-[#1D293D] text-center">
                              {row.equipmentModel}
                            </td>
                            <td className="px-3 py-3 text-[14px] text-[#1D293D] text-center">
                              {row.owner}
                            </td>
                            <td className="px-3 py-3 text-[14px] text-[#1D293D] text-center">
                              {row.contractorOrScgl}
                            </td>
                            <td className="px-3 py-3 text-[14px] text-[#1D293D] text-center">
                              <button
                                type="button"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-red-400 hover:text-red-800"
                                aria-label="Open row options"
                                onClick={() => {
                                  handleDesactivateEquipment(row);
                                }}
                              >
                                <Archive size={22} />
                              </button>
                            </td>
                          </tr>
                        ))
                      : null}
                  </tbody>
                </table>
              </div>
            </div>
            {/*Botonera para tabla */}
            {!isLoadingRows && !rowsError ? (
              <div className="flex flex-wrap items-center gap-4 px-1 pt-4 text-[14px] text-[#6A7282] px-2 py-2">
                <div className="inline-flex items-center gap-2">
                  <span>Show</span>

                  <Select
                    defaultValue={recordsPerPage}
                    onChange={setRecordsPerPage}
                    options={RECORD_COUNT_OPTIONS}
                    ClassName="h-10 w-[72px] rounded-2xl border-[#D7DEE8] px-3 text-[14px] font-medium"
                    menuPlacement="top"
                  />

                  <span>records</span>
                </div>

                <div className="ml-auto flex flex-wrap items-center gap-3">
                  <span>
                    Showing {paginatedRows.length} of {totalRecords} records
                  </span>
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setActivePage((previousPage) =>
                          Math.max(1, previousPage - 1),
                        )
                      }
                      disabled={activePage <= 1}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#D7DEE8] bg-white text-[#6A7282] disabled:cursor-not-allowed disabled:opacity-50 hover:bg-[#F8FAFC]"
                      aria-label="Previous page"
                    >
                      <ChevronLeft size={18} />
                    </button>

                    {visiblePageNumbers.map((pageNumber) => (
                      <button
                        key={pageNumber}
                        type="button"
                        onClick={() => setActivePage(pageNumber)}
                        className={
                          activePage == pageNumber
                            ? "inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#2563EB] bg-[#2563EB] text-white text-[14px] font-medium transition"
                            : "inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#D7DEE8] bg-white text-[#6A7282] text-[14px] font-medium transition hover:bg-[#F8FAFC]"
                        }
                      >
                        {pageNumber}
                      </button>
                    ))}

                    <button
                      type="button"
                      onClick={() =>
                        setActivePage((previousPage) =>
                          Math.min(totalPages, previousPage + 1),
                        )
                      }
                      disabled={activePage >= totalPages}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#D7DEE8] bg-white text-[#6A7282] disabled:cursor-not-allowed disabled:opacity-50 hover:bg-[#F8FAFC]"
                      aria-label="Next page"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </section>
        </div>
      </section>

      <NewStatusModal
        isOpen={isAddStatusOpen}
        onClose={handleCloseModal}
        onSave={handleSaveEquipment}
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="mb-2 text-[14px] font-medium text-[#6A7282]">
              Equipment Name
            </p>
            <input
              value={newStatusForm.equipmentName}
              onChange={(event) =>
                updateNewStatusField("equipmentName", event.target.value)
              }
              placeholder="AB123"
              className="h-10 w-full rounded-2xl border border-[#CAD5E2] bg-white px-4 text-[14px] font-medium text-[#4A5565] outline-none placeholder:text-[#98A2B3]"
            />
          </div>
          <ModalEquipmentTypeField
            label="Equipment Type"
            value={newStatusForm.equipmentTypeId}
            options={equipmentTypeList}
            isTop={false}
            onChange={(value) => handleChangeEquipmentTypeModal(value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <ModalEquipmentTypeField
            label="Equipment Model"
            value={newStatusForm.equipmentModelId}
            options={equipmentTypeModelModal}
            isTop={false}
            onChange={(value) =>
              updateNewStatusField("equipmentModelId", value)
            }
          />

          <ModalEquipmentTypeField
            label="Contractor/SCGL"
            value={newStatusForm.contractorScgl}
            options={DEFAULT_CONTRACTOR_SCGL}
            isTop={false}
            onChange={(value) => updateNewStatusField("contractorScgl", value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <ModalEquipmentTypeField
            label="Equipment Owner"
            value={newStatusForm.equipmentOwner}
            options={equipmentOwnerList}
            isTop={true}
            onChange={(value) => updateNewStatusField("equipmentOwner", value)}
          />
        </div>
      </NewStatusModal>

      <GenericModal
        isOpen={informativeModalIsOpen}
        title={"Decommission Equipment " + selectedEquipmentInfo?.equipment}
        type="info"
        needIcon={true}
        tooltipType={
          "If your current shift is Day Shift – AUG 08, the system will consider production data until Night Shift AUG 07"
        }
        onClose={() => handleCloseModalInformative(false)}
        footer={
          <>
            <button
              onClick={() => handleCloseModalInformative(false)}
              className="flex items-center justify-center gap-2 h-12 min-w-[160px] rounded-xl bg-[#D81D1D] px-5 text-[16px] font-semibold text-white shadow-sm hover:bg-[#C21313]"
            >
              Cancel
            </button>
            <button
              className="flex items-center justify-center gap-2 h-12 min-w-[160px] rounded-xl bg-[#2563EB] px-5 text-[16px] font-semibold text-white shadow-sm hover:bg-[#1D4ED8]"
              onClick={() => desactivateEquipment(false)}
            >
              Decommission Last Night Shift
            </button>
          </>
        }
      >
        <p>
          Are you want to decommission equipment{" "}
          {selectedEquipmentInfo?.equipment}
        </p>
      </GenericModal>

      <GenericModal
        isOpen={saveModalIsOpen}
        type="info"
        title="Add New Equipment"
        needIcon={false}
        onClose={() => setSaveModalIsOpen(false)}
        footer={
          <>
            <button
              onClick={() => setSaveModalIsOpen(false)}
              className="flex items-center justify-center gap-2 h-12 min-w-[160px] rounded-xl bg-[#D81D1D] px-5 text-[16px] font-semibold text-white shadow-sm hover:bg-[#C21313]"
            >
              Cancel
            </button>

            <button
              className="flex items-center justify-center gap-2 h-12 min-w-[160px] rounded-xl bg-[#2563EB] px-5 text-[16px] font-semibold text-white shadow-sm hover:bg-[#1D4ED8]"
              onClick={saveNewEquipment}
            >
              Add
            </button>
          </>
        }
      >
        <p>Are you sure you want to save this equipment?</p>
      </GenericModal>

      <GenericModal
        isOpen={errorModalIsOpen.isOpen}
        title={errorModalIsOpen.title}
        type={errorModalIsOpen.type}
        needIcon={false}
        onClose={() => {
          setErrorModalIsOpen(DEFAULT_INFORMATION_MODAL);
        }}
      >
        <p>{errorModalIsOpen.informationMessage}</p>
      </GenericModal>

      <GenericModal
        isOpen={modalIsLoading}
        title="Processing"
        type="info"
        needIcon={false}
      >
        <div className="flex justify-center py-6">
          <Loader2 size={40} className="animate-spin text-blue-600" />
        </div>
      </GenericModal>
    </>
  );
};
